// C:\Projects\texe-web\src\App.tsx
import { useEffect, Fragment, ReactNode, useState, useRef } from "react";
import { NavLink, Routes, Route, Link } from "react-router-dom";

import i18n from "./i18n";
import ScrollToTop from "./components/ScrollToTop";

import Home from "./pages/Home";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ProtectedRoute from "./routes/ProtectedRoute";
import AdminRoute from "./routes/AdminRoute";
import { useAuth } from "./context/AuthProvider";
import Feed from "./pages/Feed";
import NewPost from "./pages/NewPost";
import FollowingFeed from "./pages/FollowingFeed";
import UserProfile from "./pages/UserProfile";
import PostDetail from "./pages/PostDetail";
import NotificationsPage from "./pages/Notifications";
import NotificationsBell from "./components/NotificationsBell";
import MyProfileRedirect from "./pages/MyProfileRedirect";
import SettingsProfile from "./pages/SettingsProfile";
import Settings from "./pages/Settings";
import EditProfile from "./pages/EditProfile";
import BlockedUsers from "./pages/BlockedUsers";
import SearchUsers from "./pages/SearchUsers";
import InlineUserSearch from "./components/InlineUserSearch";
import TagFeed from "./pages/TagFeed";
import AdminUsernames from "./pages/AdminUsernames";
import AdminAuditPage from "./pages/AdminAuditPage";
import AdminSecurity from "./pages/AdminSecurity";
import AdminLoginAttempts from "./pages/AdminLoginAttempts";
import AdminNotificationRules from "./pages/AdminNotificationRules";
import AdminDashboard from "./pages/AdminDashboard";
import AdminReports from "./pages/AdminReports";
import AdminAnnouncements from "./pages/AdminAnnouncements";
import AdminUsers from "./pages/AdminUsers";
import AdminUsersWithPhone from "./pages/AdminUsersWithPhone";
import AdminPremium from "./pages/AdminPremium";
import AdminOutbox from "./pages/AdminOutbox";
import AdminAdsSettings from "./pages/AdminAdsSettings";

// 🔽 YENİ
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import SavnotAbout from "./pages/SavnotAbout";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";

// SavWar pages
import SavWar from "./pages/SavWar";
import SavWarTopicPage from "./pages/SavWarTopicPage";
import SavWarArchive from "./pages/SavWarArchive";
import AdminSavWar from "./pages/AdminSavWar";

// SavKabile
import Savkabile from "./pages/Savkabile";
import CreateKabile from "./pages/CreateKabile";
import MyKabile from "./pages/MyKabile";
import KabileDetail from "./pages/KabileDetail";
import KabileDailyBattle from "./pages/KabileDailyBattle";
import KabileSiralamalari from "./pages/KabileSiralamalari";
import GlobalArena from "./pages/GlobalArena";
import AdminSavKabile from "./pages/AdminSavKabile";
import AdminKabiles from "./pages/AdminKabiles";
import AdminKabilesMobile from "./pages/AdminKabilesMobile";

// Premium Shop
import PremiumShop from "./pages/PremiumShop";

import "./App.css";
import "./styles/gold.css";

function ActiveLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <NavLink
      to={to}
      style={({ isActive }) => ({
        textDecoration: "none",
        padding: "6px 10px",
        borderRadius: 8,
        background: isActive ? "#151515" : "transparent",
        border: isActive ? "1px solid var(--muted)" : "1px solid transparent",
        color: "inherit",
        whiteSpace: "nowrap",
      })}
    >
      {children}
    </NavLink>
  );
}

export default function App() {
  const { user } = useAuth();
  const [searchOpen, setSearchOpen] = useState(false);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const touchStartX = useRef(0);

  useEffect(() => {
    const lang = (user?.preferredLanguage || "tr").toLowerCase();
    try {
      i18n.changeLanguage(lang);
    } catch (err) {
      console.warn("Failed to change language:", err);
    }
    try {
      document.documentElement.lang = lang;
    } catch (err) {
      console.warn("Failed to set document lang:", err);
    }
  }, [user?.preferredLanguage]);

  const handleHeaderTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    touchStartX.current = e.touches[0]?.clientX ?? 0;
  };

  const handleHeaderTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    const deltaX = Math.abs((e.touches[0]?.clientX ?? 0) - touchStartX.current);
    if (deltaX > 4) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <div style={{ 
      minHeight: "100vh", 
      background: "var(--bg)", 
      color: "var(--text)",
      width: "100%"
    }}>
      <header
        ref={headerRef}
        className="app-header-scroll"
        style={{
          display: "flex",
          gap: 16,
          padding: 12,
          borderBottom: "1px solid var(--muted)",
          alignItems: "center",
          position: "sticky",
          top: 0,
          background: "#0a0a0a",
          backdropFilter: "blur(2px)",
          zIndex: 999,
          overflowX: "auto",
          overflowY: "hidden",
          WebkitOverflowScrolling: "touch",
          overscrollBehaviorX: "contain",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          touchAction: "pan-x",
        }}
        onTouchStart={handleHeaderTouchStart}
        onTouchMove={handleHeaderTouchMove}
      >
        {/* Logo + SavNot */}
        <Link 
          to="/savnot" 
          style={{ 
            display: "flex", 
            alignItems: "center", 
            gap: 8, 
            textDecoration: "none", 
            color: "inherit",
            marginRight: 8,
          }}
        >
          <img 
            src="/logo/savnot-icon-dark-64.png" 
            alt="SavNot" 
            style={{ 
              width: 28, 
              height: 28,
              borderRadius: 6,
              objectFit: "contain"
            }} 
          />
          <span style={{ fontWeight: 800, fontSize: 20 }}>SavNot</span>
        </Link>

        <ActiveLink to="/">🏆 SavLike</ActiveLink>
        <ActiveLink to="/savwar">⚔️ SavWar</ActiveLink>
        <ActiveLink to="/savkabile">🏛️ SavKabile</ActiveLink>
        <ActiveLink to="/feed">Akış</ActiveLink>
        <ActiveLink to="/following">Takip Akışı</ActiveLink>
        <ActiveLink to="/premium-shop">⭐ Premium</ActiveLink>
        <ActiveLink to="/profile">Profil</ActiveLink>

        {/* Masaüstü arama */}
        <div className="header-search-desktop" style={{ position: "relative", zIndex: 10000 }}>
          <InlineUserSearch />
        </div>

        {/* Mobil arama butonu */}
        <button
          className="header-search-mobile-btn"
          onClick={() => setSearchOpen(true)}
          aria-label="Kullanıcı ara"
          title="Kullanıcı ara"
        >
          🔍
        </button>

        {user && (
          <div style={{ display: "flex", gap: 10, alignItems: "center", position: "relative", zIndex: 10000 }}>
            <ActiveLink to="/new">Paylaş</ActiveLink>
            <NotificationsBell />
            <ActiveLink to="/settings">Ayarlar</ActiveLink>
          </div>
        )}

        <div style={{ display: "flex", gap: 10, alignItems: "center", marginLeft: 12 }}>
          {user ? (
            <span style={{ color: "var(--text)" }}>👋 {user.username}</span>
          ) : (
            <Fragment>
              <ActiveLink to="/login">Giriş</ActiveLink>
              <ActiveLink to="/register">Kayıt</ActiveLink>
            </Fragment>
          )}
        </div>
      </header>

      {/* Mobil arama modalı */}
      {searchOpen && (
        <>
          <div
            className="search-modal-backdrop"
            onClick={() => setSearchOpen(false)}
            aria-hidden="true"
          />
          <div className="search-modal" role="dialog" aria-modal="true" aria-label="Kullanıcı ara">
            <div className="search-modal-header">
              <h3 className="search-modal-title">Kullanıcı ara</h3>
              <button
                className="search-modal-close"
                onClick={() => setSearchOpen(false)}
                aria-label="Kapat"
              >
                Kapat
              </button>
            </div>
            <InlineUserSearch />
          </div>
        </>
      )}

      <ScrollToTop />

      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/savnot" element={<SavnotAbout />} />
          <Route path="/savwar" element={<SavWar />} />
          <Route path="/savwar/archive" element={<SavWarArchive />} />
          <Route path="/savwar/topics/:slug" element={<SavWarTopicPage />} />
          <Route path="/savwar/post/:postId" element={<PostDetail />} />
          <Route path="/savkabile" element={<Savkabile />} />
          <Route path="/savkabile/create" element={<CreateKabile />} />
          <Route path="/savkabile/my" element={<MyKabile />} />
          <Route path="/savkabile/battle" element={<KabileDailyBattle />} />
          <Route path="/savkabile/global" element={<GlobalArena />} />
          <Route path="/savkabile/rankings" element={<KabileSiralamalari />} />
          <Route path="/savkabile/:kabileId" element={<KabileDetail />} />
          <Route path="/premium-shop" element={<PremiumShop />} />
          <Route path="/feed" element={<Feed />} />
          <Route path="/following" element={<FollowingFeed />} />
          <Route path="/p/:postId" element={<PostDetail />} />
          <Route path="/post/:postId" element={<PostDetail />} />
          <Route path="/u/:username" element={<UserProfile />} />
          <Route path="/tag/:name" element={<TagFeed />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />

          {/* 🔽 YENİ: Şifre sıfırlama sayfaları */}
          <Route path="/forgot" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify" element={<VerifyEmail />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />

          <Route path="/profile" element={<MyProfileRedirect />} />
          <Route path="/profile/:userId" element={<UserProfile />} />
          <Route path="/search/users" element={<SearchUsers />} />

          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/profile"
            element={
              <ProtectedRoute>
                <SettingsProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile/edit"
            element={
              <ProtectedRoute>
                <EditProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/blocked"
            element={
              <ProtectedRoute>
                <BlockedUsers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <NotificationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/new"
            element={
              <ProtectedRoute>
                <NewPost />
              </ProtectedRoute>
            }
          />

          {/* Admin: Dashboard */}
          <Route
            path="/admin/dashboard"
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          />

          {/* Admin: Reports */}
          <Route
            path="/admin/reports"
            element={
              <AdminRoute>
                <AdminReports />
              </AdminRoute>
            }
          />

          {/* Admin: Announcements */}
          <Route
            path="/admin/announcements"
            element={
              <AdminRoute>
                <AdminAnnouncements />
              </AdminRoute>
            }
          />

          {/* Admin: Users With Phone */}
          <Route
            path="/admin/users-with-phone"
            element={
              <AdminRoute>
                <AdminUsersWithPhone />
              </AdminRoute>
            }
          />

          {/* Admin: User Management */}
          <Route
            path="/admin/users"
            element={
              <AdminRoute>
                <AdminUsers />
              </AdminRoute>
            }
          />

          {/* Admin: kullanıcı adı yönetimi */}
          <Route
            path="/admin/usernames"
            element={
              <AdminRoute>
                <AdminUsernames />
              </AdminRoute>
            }
          />

          {/* Admin: audit listesi */}
          <Route
            path="/admin/audit"
            element={
              <AdminRoute>
                <AdminAuditPage />
              </AdminRoute>
            }
          />

          {/* Admin: SavWar yönetimi */}
          <Route
            path="/admin/savwar"
            element={
              <AdminRoute>
                <AdminSavWar />
              </AdminRoute>
            }
          />

          {/* Admin: SavKabile yönetimi */}
          <Route
            path="/admin/savkabile"
            element={
              <AdminRoute>
                <AdminSavKabile />
              </AdminRoute>
            }
          />

          {/* Admin: Kabile Yönetimi */}
          <Route
            path="/admin/kabiles"
            element={
              <AdminRoute>
                <AdminKabiles />
              </AdminRoute>
            }
          />

          {/* Admin: Kabile Yönetimi (Mobile) */}
          <Route
            path="/admin/kabiles-mobile"
            element={
              <AdminRoute>
                <AdminKabilesMobile />
              </AdminRoute>
            }
          />

          {/* Admin: Security Dashboard */}
          <Route
            path="/admin/security"
            element={
              <AdminRoute>
                <AdminSecurity />
              </AdminRoute>
            }
          />

          {/* Admin: Login Attempts / Failed Logins */}
          <Route
            path="/admin/login-attempts"
            element={
              <AdminRoute>
                <AdminLoginAttempts />
              </AdminRoute>
            }
          />

          {/* Admin: Notification Rules & System Alerts */}
          <Route
            path="/admin/notification-rules"
            element={
              <AdminRoute>
                <AdminNotificationRules />
              </AdminRoute>
            }
          />

          {/* Admin: Premium Items Management */}
          <Route
            path="/admin/premium"
            element={
              <AdminRoute>
                <AdminPremium />
              </AdminRoute>
            }
          />

          {/* Admin: Outbox Management */}
          <Route
            path="/admin/outbox"
            element={
              <AdminRoute>
                <AdminOutbox />
              </AdminRoute>
            }
          />

          {/* Admin: Ads Settings */}
          <Route
            path="/admin/ads"
            element={
              <AdminRoute>
                <AdminAdsSettings />
              </AdminRoute>
            }
          />

          <Route
            path="*"
            element={
              <div style={{ padding: 24 }}>
                <h2>Sayfa bulunamadı</h2>
                <p>
                  Aradığın sayfa yok gibi görünüyor.{" "}
                  <Link to="/" style={{ color: "var(--link)" }}>
                    Ana sayfaya dön
                  </Link>
                  .
                </p>
              </div>
            }
          />
        </Routes>
      </main>
    </div>
  );
}
